import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle, Loader2, Shield, Key, LogIn, LogOut, UserPlus,
    UserMinus, Trash2, Lock, RefreshCw,
} from "lucide-react";
import FilterChip from "@/components/ui/FilterChip";
import { api, formatApiError } from "@/lib/api";
import { relTime } from "@/utils/userAgent";

const GROUPS = [
    { key: "account",      label: "Account changes",       icon: UserMinus },
    { key: "password",     label: "Password events",       icon: Key },
    { key: "lockouts",     label: "Lockouts",              icon: Lock },
    { key: "sec_admin",    label: "Sec-admin changes",     icon: Shield },
    { key: "sessions",     label: "Session events",        icon: LogOut },
    { key: "user_created", label: "User creation",         icon: UserPlus },
    { key: "user_deleted", label: "User deletion",         icon: Trash2 },
    { key: "purge",        label: "Permanent purge",       icon: Trash2 },
];

function eventTone(event) {
    if (!event) return "neutral";
    if (event.includes("failure") || event.includes("invalid") || event.includes("locked")) return "amber";
    if (event.includes("revoked") || event.includes("disabled") || event.includes("deleted") || event.includes("purge")) return "red";
    if (event.includes("granted") || event.includes("reactivated")) return "green";
    return "neutral";
}

function toneCls(tone) {
    return {
        neutral: "text-[#131B2F] bg-[#F5F5F7]",
        amber:   "text-[#B45309] bg-[#FEF3C7]",
        red:     "text-[#991B1B] bg-[#FEE2E2]",
        green:   "text-[#2E7E22] bg-[#E6F1DE]",
    }[tone] || "text-[#131B2F] bg-[#F5F5F7]";
}

/**
 * Audit feed for a selected user (default) with a "view all" toggle
 * that removes the user scope and shows global events instead.
 */
export default function AuditTab({ userId, userLabel }) {
    const [group, setGroup] = useState(null); // null = all groups
    const [scope, setScope] = useState("user"); // "user" | "all"
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (group) params.group = group;
            if (scope === "user" && userId) params.user_id = userId;
            setRows(await api.adminQueryAudit(params));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load audit events");
            setRows([]);
        } finally { setLoading(false); }
    }, [group, scope, userId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="bg-white rounded-xl border border-[#E5E7EB]" data-testid="admin-sec-audit-tab">
            <div className="px-4 md:px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-[#131B2F]">Security audit</div>
                    <div className="text-[11px] text-[#4B5565]">
                        {scope === "user" ? `Events scoped to ${userLabel || "this user"}` : "Global audit feed"}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-md border border-[#E5E7EB] p-0.5 bg-white text-[11px]">
                        <button
                            type="button"
                            onClick={() => setScope("user")}
                            className={`h-7 px-2.5 rounded ${scope === "user" ? "bg-[#131B2F] text-white" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}
                            data-testid="admin-sec-audit-scope-user"
                        >
                            This user
                        </button>
                        <button
                            type="button"
                            onClick={() => setScope("all")}
                            className={`h-7 px-2.5 rounded ${scope === "all" ? "bg-[#131B2F] text-white" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}
                            data-testid="admin-sec-audit-scope-all"
                        >
                            View all
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex items-center h-8 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]"
                        title="Reload"
                        data-testid="admin-sec-audit-reload"
                    >
                        <RefreshCw size={12} strokeWidth={1.75} />
                    </button>
                </div>
            </div>

            <div className="px-4 md:px-5 py-3 border-b border-[#E5E7EB] bg-[#FAFAFC] flex flex-wrap gap-1.5">
                <FilterChip
                    active={group === null}
                    onClick={() => setGroup(null)}
                    testid="admin-sec-audit-group-all"
                >
                    All events
                </FilterChip>
                {GROUPS.map((g) => (
                    <FilterChip
                        key={g.key}
                        active={group === g.key}
                        icon={g.icon}
                        onClick={() => setGroup(group === g.key ? null : g.key)}
                        testid={`admin-sec-audit-group-${g.key}`}
                    >
                        {g.label}
                    </FilterChip>
                ))}
            </div>

            <div className="max-h-[520px] overflow-y-auto">
                {loading ? (
                    <div className="p-6 text-center text-xs text-[#4B5565]"><Loader2 size={14} className="inline animate-spin" /> Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-xs text-[#4B5565]">No events match the current filters.</div>
                ) : (
                    <ul className="divide-y divide-[#F0F1F5]" data-testid="admin-sec-audit-list">
                        {rows.map((e, idx) => {
                            const tone = eventTone(e.event);
                            const outcome = e.outcome || (tone === "amber" ? "warning" : "success");
                            return (
                                <li key={`${e.timestamp}-${idx}`} className="px-4 md:px-5 py-2.5 flex items-start gap-3 text-xs">
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] shrink-0 ${toneCls(tone)}`}>
                                        {e.event}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[#131B2F] font-medium">{relTime(e.timestamp)}</span>
                                            <span className="text-[#4B5565]">·</span>
                                            <span className="text-[#4B5565] truncate" title={e.timestamp}>{e.timestamp}</span>
                                            {outcome && outcome !== "success" && (
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#B45309]">
                                                    <AlertTriangle size={10} /> {outcome}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 text-[#4B5565] flex flex-wrap gap-x-3 gap-y-0.5">
                                            {e.email && <span>User: {e.email}</span>}
                                            {e.admin_id && <span>Actor admin: {e.admin_id.slice(0, 8)}…</span>}
                                            {e.ip && <span>IP {e.ip}</span>}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
